import type { TimePeriodPresets, TimeReportConfig } from '@hygge/shared';
import { toLocalDayKey } from '@/lib/date';
import type { ReportGroupLevel } from './report-builder-state';
import { customRange, presetRange, type DateWindow } from './dashboard-data';

/** The Reports screen's period control, as it has always been spelled. */
export type ReportsPeriodPreset =
  | 'this-month'
  | 'last-month'
  | 'this-week'
  | 'last-week'
  | 'custom';

export interface ReportsTabState {
  period: ReportsPeriodPreset;
  customFrom: Date | null;
  customTo: Date | null;
  /** Outer → inner, in the order the reader picked them. */
  levels: readonly ReportGroupLevel[];
  /** A project id, or 'all'. */
  projectId: string;
  /** An employee id, or 'all'. [HW-955] */
  employeeId: string;
}

/**
 * The Reports screen's controls as the configuration the Builder's runner takes.
 *
 * The summarized shapes on this screen — the three presets and anything the
 * Group By picker can compose — are served by the same server aggregation the
 * Builder uses, `POST /time-entries/report/run`, rather than by a second
 * implementation of the arithmetic in this screen's own endpoint. That endpoint
 * lists entries, silently capped at 10 000 and ordered oldest-first, and has no
 * second grouping level; the Builder's runner has both levels, group totals
 * computed where the day counts are right, and no cap. One place computes the
 * numbers an invoice is cut from. [HW-877]
 *
 * Kept pure so the mapping is testable: how a preset and a custom range are
 * spelled, when a project filter is sent, and what makes the whole thing
 * unrunnable.
 *
 * A preset is sent as the days the company calendar names for it (`periods`,
 * from `GET /time-entries/periods`) — the very days the entry list is bounded
 * by ({@link reportsWindow}) and the Dashboard shows. Sending the name instead
 * let the server resolve it again at request time: on a page left open across
 * the company's midnight the summary moved to the new month while the list,
 * still holding the calendar it was given, stayed on the old one, and one
 * label named two months. One answer for every half removes that. Only when
 * the calendar could not be read (`periods` undefined) is the name sent, for
 * the server to resolve. [HW-929]
 */
export function buildReportsTabConfig(
  state: ReportsTabState,
  periods?: TimePeriodPresets,
): TimeReportConfig | null {
  if (state.levels.length === 0) return null;

  let period: TimeReportConfig['period'];
  if (state.period === 'custom') {
    if (!state.customFrom || !state.customTo) return null;
    if (state.customFrom.getTime() > state.customTo.getTime()) return null;
    // The pickers hand back local calendar dates. `toLocalDayKey` writes the
    // date the reader picked, not the UTC day its midnight falls on — east of
    // UTC those differ, and "1 September" must not be sent as 31 August.
    period = {
      preset: 'custom',
      from: toLocalDayKey(state.customFrom),
      to: toLocalDayKey(state.customTo),
    };
  } else if (periods) {
    const days = periods.presets[state.period];
    period = { preset: 'custom', from: days.from, to: days.to };
  } else {
    period = { preset: state.period };
  }

  return {
    period,
    filters: {
      billable: 'all',
      ...(state.projectId !== 'all' ? { projectIds: [state.projectId] } : {}),
      ...(state.employeeId !== 'all' ? { employeeIds: [state.employeeId] } : {}),
    },
    groupBy: [...state.levels],
    // Hours only. The three presets are described in hours, and the share and
    // daily-average columns are the Builder's to offer — this screen is the
    // quick view.
    metrics: ['hours'],
    view: 'table',
  };
}

/**
 * The `from`/`to` the Reports screen's entry listing asks for: the days the
 * company calendar names for the preset (`periods`, from
 * `GET /time-entries/periods`) — the very days the summarized half is sent
 * (see {@link buildReportsTabConfig}) — or the picked days of a custom range. Sent as whole UTC-digit days, because the
 * server files an entry under `started_at AT TIME ZONE 'UTC'`, the author's
 * wall day [HW-916]. Null until the company calendar has answered, and for a
 * half-filled or reversed custom range. [HW-929]
 */
export function reportsWindow(
  state: Pick<ReportsTabState, 'period' | 'customFrom' | 'customTo'>,
  periods: TimePeriodPresets | undefined,
): DateWindow | null {
  return state.period === 'custom'
    ? customRange(state.customFrom, state.customTo)
    : presetRange(state.period, periods);
}

/**
 * Whether the summary holds off until the company calendar has answered: it
 * runs on those days (see {@link buildReportsTabConfig}), so running first by
 * name would fetch the same report twice on every cold load. A failed calendar
 * does not hold it — the name is sent and the server resolves it. A custom
 * range never waits. [HW-929]
 */
export function summaryWaitsForPeriods(
  period: ReportsPeriodPreset,
  periods: TimePeriodPresets | undefined,
  periodsFailed: boolean,
): boolean {
  return period !== 'custom' && !periods && !periodsFailed;
}
